import React, { useEffect, useRef, useState } from "react";
import { observer } from "@legendapp/state/react";
import { Navigate, useSearchParams } from "react-router-dom";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@/hooks/UseAuthProviders";
import { getUserStats, callWithAuth } from "@/api/Api";
import {
  useActivationJourney,
  isFeatureUnlocked,
  markFeedOpened,
} from "@/features/activation";
import { useUserSettings } from "@/hooks/useUserSettings";
import {
  generatedActivities$,
  lessonDataCache$,
} from "../feed/state/videoState";
import { useFeedData } from "../feed/hooks/useFeedData";
import { VerticalVideoCarousel } from "../feed/features/VerticalVideoCarousel";
import {
  feedCurrentIndex$,
  chapters$,
  feedHasMore$,
  requestNextChapter,
  requestPreviousChapter,
  resetFeedPosition,
  jumpToChapter,
} from "../feed/state/feedState";
import { setChapterLiked } from "../feed/state/userState";
import {
  FeedLockedOverlay,
  UNLOCK_SEEN_KEY,
} from "../feed/features/FeedLockedOverlay";
import { FeedTagsOnboarding, TAGS_SEEN_KEY } from "../feed/features/FeedTagsOnboarding";
import { FeedDemoOverlay, DEMO_SEEN_KEY } from "../feed/features/FeedDemoOverlay";
import {
  FeedTutorialCoachmark,
  COACHMARK_SEEN_KEY,
} from "../feed/features/FeedTutorialCoachmark";
import { useTrackFeedEvents } from "../feed/hooks/useTrackFeedEvents";

const FeedPage: React.FC = observer(function FeedPage() {
  const { trackEvent } = useTrackFeedEvents();
  // Free users are blocked from the feed (iOS LearnFeedScreen routes role==='free'
  // → /upgrade). Same role check the lesson + review surfaces already use.
  const { user, isLoading: userLoading } = useUser();
  const isFree = user?.role === "free";

  // Source the feed_session_ended enrichment exactly as iOS LearnFeedScreen does:
  // streak_days ← userStats.dayStreak, user_level ← user.placementResult. Held in
  // refs so the session-end cleanup (effect teardown) reads the latest values
  // without re-running the start/end effect (mirrors iOS userStatsRef/userRef).
  const userStatsQuery = useQuery({
    queryKey: ["userStats"],
    queryFn: () => callWithAuth(getUserStats),
  });
  const sessionEndCtxRef = useRef<{ streakDays?: number; userLevel?: string }>({});
  sessionEndCtxRef.current = {
    streakDays: userStatsQuery.data?.dayStreak,
    userLevel: user?.placementResult,
  };

  // --- Activation gate (replaces the old isFree paywall) ---
  const journey = useActivationJourney();
  const isUnlocked = isFeatureUnlocked("feed", journey);
  // Settings drive the activation step; until they load, `useActivationJourney`
  // defaults `currentStep` to 1 (i.e. "locked"). We must NOT treat that loading
  // default as a genuine locked state, or every already-unlocked user would see a
  // spurious "Feed Unlocked!" celebration on every cold load. Gate on settings.
  const { settings } = useUserSettings();
  const settingsLoaded = settings != null;

  // The feed query only runs once the feed is actually reachable — iOS never
  // mounts <Feed/> (and thus never fetches) while locked or for free users.
  // Gate on the user role being LOADED too: while it's loading `user` is
  // undefined so `isFree` is false, and without this guard the query would fire
  // for a not-yet-known free user before the role resolves + redirect runs.
  const { feedQuery, toggleLike, markViewed, loadMore } = useFeedData({
    enabled: !userLoading && settingsLoaded && isUnlocked && !isFree,
  });

  // First-unlock celebration: if the feed is now unlocked but the celebration
  // hasn't been shown, keep the overlay mounted one last time in celebration
  // mode, then dissolve it. `wasLocked` is seeded from the FIRST real (settings-
  // loaded) evaluation so the loading-state flash can't fake a locked→unlocked
  // transition.
  const wasLocked = useRef(false);
  const seededRef = useRef(false);
  const [celebrating, setCelebrating] = useState(false);
  useEffect(() => {
    if (!settingsLoaded) return; // wait for the real activation state
    if (!seededRef.current) {
      seededRef.current = true;
      wasLocked.current = !isUnlocked; // seed from real state; don't celebrate now
      return;
    }
    if (
      isUnlocked &&
      wasLocked.current &&
      localStorage.getItem(UNLOCK_SEEN_KEY) !== "true"
    ) {
      setCelebrating(true);
      trackEvent("feed_unlocked");
    }
    if (isUnlocked) wasLocked.current = false;
  }, [isUnlocked, settingsLoaded, trackEvent]);

  // Liked-video handoff: the Liked tab navigates to
  // /home/feed?chapterId=X&lessonIds=a,b&title=… Once the feed has loaded,
  // splice that chapter in after the current position (synthesized from its
  // lessonIds when it isn't in the loaded page), mark it liked, then clear the
  // params so a refresh/back doesn't re-trigger the jump. Mirrors iOS
  // FeedNew.tsx:341-395 (targetChapter reposition + mark liked).
  const [searchParams, setSearchParams] = useSearchParams();
  const targetChapterId = searchParams.get("chapterId");
  const targetLessonIds = searchParams.get("lessonIds");
  const targetTitle = searchParams.get("title");
  const jumpedToRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isUnlocked || feedQuery.isPending) return;
    if (targetChapterId == null) return;
    const id = Number(targetChapterId);
    if (!Number.isFinite(id) || jumpedToRef.current === id) return;
    const lessonIds = (targetLessonIds ?? "")
      .split(",")
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
    if (jumpToChapter(id, { lessonIds, title: targetTitle ?? undefined })) {
      jumpedToRef.current = id;
      setChapterLiked(id, true);
      const next = new URLSearchParams(searchParams);
      next.delete("chapterId");
      next.delete("lessonIds");
      next.delete("title");
      setSearchParams(next, { replace: true });
    }
  }, [
    isUnlocked,
    feedQuery.isPending,
    targetChapterId,
    targetLessonIds,
    targetTitle,
    searchParams,
    setSearchParams,
  ]);

  // Session telemetry fires at screen level regardless of the lock state
  // (iOS LearnFeedScreen useFocusEffect).
  useEffect(() => {
    // Top-of-funnel page-enter event, distinct from feed_session_started (iOS
    // useTrackFeedEvents feed_page_viewed).
    trackEvent("feed_page_viewed");
    trackEvent("feed_session_started");
    return () => {
      const { streakDays, userLevel } = sessionEndCtxRef.current;
      trackEvent("feed_session_ended", {
        ...(streakDays !== undefined && { streak_days: streakDays }),
        ...(userLevel && { user_level: userLevel }),
      });
    };
  }, [trackEvent]);

  // Settings-driven refresh: when the user changes their dialect, CEFR level,
  // Video Topics, or the Feed Activities settings (enabled toggle / activity
  // count), the personalized feed must be regenerated (mirrors iOS
  // feedNeedsRefresh → resetFeed; iOS FeedTopicsScreen calls markFeedNeedsRefresh
  // after saving tags, and FeedActivitiesSettingsScreen sets
  // feedStore$.pendingSettingsRefresh on areFeedActivitiesEnabled / activityCount
  // change). We watch the relevant settings and refetch + clear the
  // client-generated activity cache when they change after the first load.
  const dialect = settings?.userFeedDialect;
  const cefrLevel = settings?.userFeedLevel;
  const activitiesEnabled = settings?.areFeedActivitiesEnabled;
  const activityCount = settings?.activityCount;
  // Order-independent signature for the topic set so re-ordering doesn't refetch.
  const tagsSig = [...(settings?.userFeedTags ?? [])].sort().join(",");
  const lastFeedSig = useRef<string | null>(null);
  useEffect(() => {
    if (settings == null) return;
    const sig = `${dialect ?? ""}|${cefrLevel ?? ""}|${tagsSig}|${
      activitiesEnabled ?? ""
    }|${activityCount ?? ""}`;
    if (lastFeedSig.current === null) {
      lastFeedSig.current = sig;
      return;
    }
    if (lastFeedSig.current !== sig) {
      lastFeedSig.current = sig;
      // Mirror iOS resetFeed: drop the client-generated activity cache AND the
      // prefetched lesson-data cache so the refetched feed re-fetches fresh
      // lesson content rather than serving stale cached lessons.
      generatedActivities$.set({});
      lessonDataCache$.set({});
      // iOS clearFeedStore also resets the feed position + per-chapter active
      // lesson index (chapterIDtoActiveLessonIdx) so the refreshed feed starts
      // at the top rather than mid-chapter on a now-stale lesson.
      resetFeedPosition();
      void feedQuery.refetch();
    }
  }, [
    settings,
    dialect,
    cefrLevel,
    tagsSig,
    activitiesEnabled,
    activityCount,
    feedQuery,
  ]);

  // Tags onboarding: shown once on first open for unlocked users.
  const [tagsSeen, setTagsSeen] = useState(
    () => !!localStorage.getItem(TAGS_SEEN_KEY),
  );

  // Demo overlay: shown once after tags onboarding is done.
  const [demoSeen, setDemoSeen] = useState(
    () => !!localStorage.getItem(DEMO_SEEN_KEY),
  );

  // In-video word-click / phrase-highlight coachmarks (iOS FeedNew tutorialPhase).
  // First-time only, gated behind the swipe/like demo so the phase ordering
  // matches native (feed demo → word/phrase tutorial).
  const [coachmarkSeen, setCoachmarkSeen] = useState(
    () => !!localStorage.getItem(COACHMARK_SEEN_KEY),
  );

  // Mark the feed opened (completes activation step 6) only once the actual
  // feed content is reachable — not while locked or in tags onboarding (iOS
  // LearnFeedScreen feedContentVisible).
  useEffect(() => {
    if (!isUnlocked || !tagsSeen) return;
    markFeedOpened();
  }, [isUnlocked, tagsSeen]);

  const currentIndex = feedCurrentIndex$.get();
  const chapters = chapters$.get();
  const hasMore = feedHasMore$.get();
  const isFirst = currentIndex === 0;
  // The last navigable slot is the end-of-content screen once the feed is
  // exhausted (virtual index === chapters.length); otherwise the last chapter.
  const lastIndex = hasMore ? chapters.length - 1 : chapters.length;
  const isLast = currentIndex >= lastIndex;

  // Free-user paywall gate (iOS parity). Until user data loads, `isFree` is
  // false, so there's no premature redirect.
  if (isFree) {
    return <Navigate to="/upgrade" replace />;
  }

  if (feedQuery.isError) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-black">
        <p className="text-white/70 text-sm text-center px-6">
          Failed to load feed. Check your connection.
        </p>
      </div>
    );
  }

  // First-unlock celebration overlays whatever body the user is about to see
  // (iOS FeedUnlockCelebrationOverlay sits over onboarding or the feed itself).
  const celebrationOverlay = celebrating ? (
    <FeedLockedOverlay
      journey={journey}
      onCelebrationComplete={() => setCelebrating(false)}
    />
  ) : null;

  // Until the real activation step is known, show the loading spinner — the
  // default "step 1" while settings load must not flash the locked gate at
  // already-unlocked users (iOS hydrates userSettings$ before this mounts).
  if (!settingsLoaded) {
    return (
      <div className="h-full w-full bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
      </div>
    );
  }

  // Locked: render ONLY the overlay — the feed must not mount underneath, so
  // no playback, no chapter-viewed posts, no prefetches happen while locked
  // (iOS LearnFeedScreen returns early with FeedLockedOverlay).
  if (!isUnlocked) {
    return (
      <div className="h-full w-full bg-black relative flex flex-col">
        <div className="flex-1 min-h-0 relative">
          <FeedLockedOverlay journey={journey} />
        </div>
      </div>
    );
  }

  // Tags onboarding replaces the feed body (iOS shows FeedTagsOnboarding
  // INSTEAD of <Feed/>), so locked-out fetch/playback can't run behind it.
  if (!tagsSeen) {
    return (
      <div className="h-full w-full bg-black relative flex flex-col">
        <div className="flex-1 min-h-0 relative">
          <FeedTagsOnboarding
            onDismiss={() => setTagsSeen(true)}
            onTagsSaved={() => feedQuery.refetch()}
          />
          {celebrationOverlay}
        </div>
      </div>
    );
  }

  // PARROT-544 #2: the first-run feed intro animation — the swipe/like demo
  // overlay (FeedDemoOverlay) chained into the tap-words/highlight-phrases
  // coachmarks (FeedTutorialCoachmark) — is janky/broken on web. Disabled for
  // now pending a rework; flip this back to re-enable the whole intro sequence.
  const FEED_INTRO_TUTORIAL_DISABLED = true;
  const showDemo = !FEED_INTRO_TUTORIAL_DISABLED && !demoSeen && !celebrating;
  const showCoachmark =
    !FEED_INTRO_TUTORIAL_DISABLED && demoSeen && !coachmarkSeen && !celebrating;

  return (
    <div className="h-full w-full bg-black relative flex flex-col">
      <div className="flex-1 min-h-0 relative">
        {feedQuery.isPending ? (
          <div className="h-full flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
          </div>
        ) : (
          <VerticalVideoCarousel
            onToggleLike={(chapterId, currentlyLiked) => toggleLike(chapterId, currentlyLiked)}
            onChapterViewed={(chapterId) => markViewed(chapterId)}
            onLoadMore={() => {
              trackEvent("feed_load_more");
              void loadMore();
            }}
          />
        )}

        {/* Demo overlay — shown after tags onboarding is complete */}
        {showDemo && (
          <FeedDemoOverlay onDismiss={() => setDemoSeen(true)} />
        )}

        {/* In-video word/phrase coachmarks — shown after the swipe/like demo */}
        {showCoachmark && (
          <FeedTutorialCoachmark onComplete={() => setCoachmarkSeen(true)} />
        )}

        {celebrationOverlay}
      </div>

      {/* Desktop nav arrows — right edge. Only while the carousel is mounted:
          the arrows drive it via feedVerticalScrollRequest$, so rendering them
          during the loading spinner (carousel unmounted) would leave them inert. */}
      {!feedQuery.isPending && (
      <div className="hidden md:flex fixed right-4 top-1/2 -translate-y-1/2 flex-col items-center gap-3 z-50">
        <button
          type="button"
          onClick={requestPreviousChapter}
          disabled={isFirst}
          className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white transition-all duration-150 active:scale-90 disabled:opacity-30 disabled:pointer-events-none hover:bg-white/20"
          aria-label="Previous video"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={requestNextChapter}
          disabled={isLast}
          className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white transition-all duration-150 active:scale-90 disabled:opacity-30 disabled:pointer-events-none hover:bg-white/20"
          aria-label="Next video"
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>
      )}
    </div>
  );
});

export default FeedPage;
